import { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import LearningPath from '@/components/LearningPath';
import RightPanel from '@/components/RightPanel';
import StatsBar from '@/components/StatsBar';
import LearningPackViewer from '@/components/LearningPackViewer';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
// Note: ChatPage and CuriosityCentre are separate routes, not rendered here
import { useLearningPack } from '@/hooks/useLearningPack';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getStudentTeacherNotes } from '@/api/teacherNotes';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { getPortalType, getAuthToken, getUserData } from '@/utils/auth';

export default function LearnPage() {
  const rightPanelRef = useRef(null);

  // Navigation request state for LearningPath
  const [navigationRequest, setNavigationRequest] = useState(null);

  const handleNavigateToAnnotation = (annotationId) => {
    setIsLearningPackOpen(false);
    setNavigationRequest({ type: 'annotation', id: annotationId, timestamp: Date.now() });
  };

  // Mobile split state: top = PDF/content, bottom = chat/right-panel
  const [mobileShowPdf, setMobileShowPdf] = useState(true);
  const [mobileJourneyFullscreen, setMobileJourneyFullscreen] = useState(false);

  // Mobile collapse states
  const [learningPathCollapsed, setLearningPathCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true); // Start with progress collapsed (learning path full screen)

  // Track if PDF/textbook viewer is open
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  // Callback for when textbook opens/closes - only set to true, never to false
  const handleTextbookOpen = useCallback((isOpen) => {
    if (isOpen) {
      setIsPdfViewerOpen(true);
    }
    // Don't set to false to avoid issues with layout changes
  }, []);

  // Callback for when textbook is closed by user
  const handleTextbookClose = useCallback(() => {
    setIsPdfViewerOpen(false);
  }, []);

  // Mobile collapse handlers
  const handleLearningPathToggle = () => {
    if (!learningPathCollapsed && rightPanelCollapsed) {
      // If collapsing Learning Path and Right Panel is collapsed, expand Right Panel
      setRightPanelCollapsed(false);
    }
    setLearningPathCollapsed(!learningPathCollapsed);
  };

  const handleRightPanelToggle = () => {
    if (!rightPanelCollapsed && learningPathCollapsed) {
      // If collapsing Right Panel and Learning Path is collapsed, expand Learning Path
      setLearningPathCollapsed(false);
    }
    setRightPanelCollapsed(!rightPanelCollapsed);
  };

  // New toggle for progress button - now handles chat and progress panel
  const handleProgressToggle = () => {
    if (chatMode) {
      // When chat is open, close it
      setChatMode(false);
    } else {
      // When chat is closed, toggle progress panel
      if (rightPanelCollapsed) {
        // Show progress: collapse learning path, expand right panel
        setLearningPathCollapsed(true);
        setRightPanelCollapsed(false);
      } else {
        // Show previous view: expand learning path, collapse right panel
        setLearningPathCollapsed(false);
        setRightPanelCollapsed(true);
      }
    }
  };

  // Use PointsContext for XP instead of local state
  const { points, refreshPoints } = usePoints();
  const [totalXP, setTotalXP] = useState(0); // Keep for backward compatibility with handleXPEarned
  const [journeyMode, setJourneyMode] = useState(false);
  const [currentJourneyIndex, setCurrentJourneyIndex] = useState(0);
  const [chatMode, setChatMode] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [prefilledAnchor, setPrefilledAnchor] = useState(null);
  const [prefillAndSendText, setPrefillAndSendText] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh of components
  const [citationHighlights, setCitationHighlights] = useState([]);
  const [citationHighlightedHighlightId, setCitationHighlightedHighlightId] = useState(null);
  const [citationTargetPage, setCitationTargetPage] = useState(1);
  const [isLearningPackOpen, setIsLearningPackOpen] = useState(false);
  const [learningPackActivities, setLearningPackActivities] = useState([]);
  const [learningPackCurrentIndex, setLearningPackCurrentIndex] = useState(0);
  const [mergedSectionKeys, setMergedSectionKeys] = useState(null); // For multi-section accordion expansion

  // Track previous view for button display
  const [previousView, setPreviousView] = useState('Learning Path'); // 'Learning Path', 'PDF Section Viewer', or 'Card View Journey'

  // Update previous view when states change
  useEffect(() => {
    if (journeyMode) {
      setPreviousView('Card View Journey');
    } else if (isPdfViewerOpen) {
      setPreviousView('PDF Section Viewer');
    } else {
      setPreviousView('Learning Path');
    }
  }, [journeyMode, isPdfViewerOpen]);

  // Teacher Notes State (Lifted from RightPanel)
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [teacherNotes, setTeacherNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);

  // Mobile selection panel state
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(false);

  // Subjects and chapters for selection modal
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  // Use context for subject/chapter selection
  const {
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    updateSubject,
    updateChapter,
    updateSelection
  } = useSubjectChapter();

  // Learning pack state for right panel - now using prop-based data instead of static
  const packState = useLearningPack(null);

  const handleSelectionChange = (selection, chapterId = null) => {
    // If user changes selection while a learning pack is open, close it to prevent mismatched activities.
    if (isLearningPackOpen) {
      setIsLearningPackOpen(false);
      setLearningPackActivities([]);
    }

    // Update selection in context
    if (selection && selection.subject && selection.chapter) {
      // When both subject and chapter are present, use the new subject id if available
      updateSelection(selection.subjectId, selection.subject, chapterId, selection.chapter);
    } else if (selection && selection.subject) {
      // Use the new subject id if provided in selection
      updateSubject(selection.subjectId, selection.subject);
    } else if (chapterId) {
      updateChapter(chapterId, selection.chapter ?? '');
    }
  };

  const handleSubjectChange = (value) => {
    const subject = subjects.find(s => s.id.toString() === value);
    updateSubject(subject?.id || null, subject?.label || '');
    updateChapter(null, '');
  };

  const handleChapterChange = (value) => {
    const chapter = chapters.find(c => c.id.toString() === value);
    updateChapter(chapter?.id || null, chapter?.label || '');
  };

  const handleXPEarned = (xpAmount) => {
    // Update local state for immediate UI feedback
    setTotalXP(prev => prev + xpAmount);
    // Refresh points from backend to get accurate data
    refreshPoints();
  };

  const handleStartJourney = () => {
    // Check if valid selection
    if (selectedChapterId) {
      setJourneyMode(true);
      setCurrentJourneyIndex(0);
      try { trackEvent('learning_path_started', attachUserProps({ subject_id: selectedSubjectId, chapter_id: selectedChapterId, page: 'learn' })); } catch (e) { }
      return true;
    }
    return false; // Invalid selection
  };

  const handleJourneyModeChange = (isActive) => {
    setJourneyMode(isActive);
    if (!isActive) {
      setCurrentJourneyIndex(0);
      setLearningPackActivities([]); // Clear activities when journey ends
    }
  };

  // Fetch subjects and chapters for selection modal
  useEffect(() => {
    const loadSubjectsAndChapters = async () => {
      try {
        const portal = getPortalType(window.location.pathname || '');
        const userData = getUserData(portal);

        if (!userData || !userData.board || !userData.grade) {
          return;
        }

        const boardId = userData.board.id;
        const gradeId = userData.grade.id;

        // Fetch subjects
        const subjectsList = await subjectsAPI.list({ board: boardId, grade: gradeId });
        const subjectsFormatted = (subjectsList || []).map(s => ({
          value: s.id.toString(),
          label: s.subject_name,
          id: s.id
        }));
        setSubjects(subjectsFormatted);
      } catch (error) {
        console.error('Error loading subjects:', error);
      }
    };

    loadSubjectsAndChapters();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedSubjectId) {
        setChapters([]);
        return;
      }

      try {
        const chaptersList = await chaptersAPI.list({ subject: selectedSubjectId });
        const chaptersFormatted = (chaptersList || []).map(c => ({
          value: c.id.toString(),
          label: c.chapter_name,
          id: c.id,
          file_url: c.file_url,
          file_upload: c.file_upload
        }));
        setChapters(chaptersFormatted);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };

    loadChapters();
  }, [selectedSubjectId]);

  // Fetch Teacher Notes when chapterId changes
  useEffect(() => {
    const fetchNotes = async () => {
      if (!selectedChapterId) {
        setTeacherNotes([]);
        return;
      }
      setIsLoadingNotes(true);
      try {
        const notes = await getStudentTeacherNotes(selectedChapterId);
        setTeacherNotes(notes || []);
      } catch (error) {
        console.error("Failed to fetch teacher notes:", error);
      } finally {
        setIsLoadingNotes(false);
      }
    };
    fetchNotes();
  }, [selectedChapterId]);

  // Learning pack activity items are loaded deeper in the flow (e.g. from chapter annotations).
  // RightPanel can render progress from chapterId without requiring a local `learningPack` object here.
  const learningPackData = journeyMode ? { items: learningPackActivities } : null;

  // Determine if the right panel should be expanded to 50%
  // This applies for Chat Mode, Teacher Notes, and Learning Pack (Short Notes)
  const isPanelExpanded = chatMode || showNotesPanel || isLearningPackOpen;

  // Resizing effect for expanded modes
  useLayoutEffect(() => {
    const handleResize = () => {
      const panel = rightPanelRef.current;
      if (panel) {
        if (isPanelExpanded) {
          panel.resize(25);
        } else {
          // When closing expanded mode, let CSS handle the width via 'flex-none' and width: 24rem
          panel.resize(28);
        }
      }
    };
    handleResize();
  }, [isPanelExpanded]);

  const handleToggleChatMode = () => {
    setChatMode(!chatMode);
    try { trackEvent('learn_toggle_chat_mode', attachUserProps({ chapter_id: selectedChapterId, page: 'learn', chat_mode: !chatMode })); } catch (e) { }
  };

  const handleOpenLearningPack = (activities, startIndex = 0) => {
    // When opening an activity from progress panel, start journey mode and close progress panel
    if (rightPanelCollapsed === false) {
      // Close the progress panel
      setRightPanelCollapsed(true);
      setLearningPathCollapsed(false);
    }
    // Store activities for journey mode
    const safeActivities = Array.isArray(activities) ? activities : [];
    setLearningPackActivities(safeActivities);
    setCurrentJourneyIndex(startIndex || 0);
    // Start journey mode to show CardJourneyView
    setJourneyMode(true);
    try { trackEvent('learning_pack_opened_from_progress', attachUserProps({ chapter_id: selectedChapterId, activity_count: safeActivities.length, page: 'learn' })); } catch (e) { }
  };

  // Handle PDF scroll - update right panel accordion to match visible section
  const handlePDFSectionScroll = useCallback((scrolledSection) => {
    console.log('🚨 [LearnPage] handlePDFSectionScroll - now using SIMPLIFIED approach:', scrolledSection);

    // NEW SIMPLIFIED APPROACH: Just set the section keys directly
    // RightPanel/ActivityProgressList will handle expanding the right sections

    if (scrolledSection.isMerged && scrolledSection.sectionName) {
      // Merged section: extract individual section names
      const sectionNames = scrolledSection.sectionName
        .split(' & ')
        .map(name => name.trim())
        .filter(Boolean);

      // Create section keys for all merged sections
      const sectionKeys = sectionNames.map(name => `name:${name}`);

      console.log(`📜 [Scroll] PDF at MERGED section: "${scrolledSection.sectionName}", setting keys:`, sectionKeys);
      setMergedSectionKeys(sectionKeys);
    } else {
      // Single section: just set the key
      const sectionKey = `name:${scrolledSection.sectionName}`;

      console.log(`📜 [Scroll] PDF at section: "${scrolledSection.sectionName}", setting key:`, sectionKey);
      setMergedSectionKeys([sectionKey]); // Still use array for consistency
    }
  }, []); // No dependencies needed!



  const handleCloseLearningPack = () => {
    setIsLearningPackOpen(false);
    try { trackEvent('learning_pack_closed', attachUserProps({ chapter_id: selectedChapterId, page: 'learn' })); } catch (e) { }
  };

  const handleCloseChatMode = () => {
    setChatMode(false);
  };

  const handleAskBudleeAI = (selectedText, teacherNoteId = null, anchor = null) => {
    // Open the right-side chat pane and pass the selected context.
    setChatMode(true);
    
    // Check if the selectedText starts with "Explain:" to auto-send
    if (selectedText && selectedText.trim().startsWith('Explain:')) {
      // Extract the actual text after "Explain:"
      const textToExplain = selectedText.replace(/^Explain:\s*"?/, '').replace(/"?$/, '').trim();
      setPrefilledText(textToExplain);
      setPrefilledAnchor(anchor);
      // Use a slight delay to ensure chat panel is ready
      setTimeout(() => {
        // Trigger auto-send by setting a hidden state or calling the send function
        // The chat panel will pick this up and auto-send
        window.dispatchEvent(new CustomEvent('autoSendExplain', {
          detail: { text: `Please explain: "${textToExplain}"` }
        }));
      }, 100);
    } else {
      setPrefilledText(selectedText);
      setPrefilledAnchor(anchor);
    }
    
    // Store teacherNoteId in sessionStorage for chat to pick up
    if (teacherNoteId !== null) {
      sessionStorage.setItem('currentTeacherNoteId', String(teacherNoteId));
    } else {
      sessionStorage.removeItem('currentTeacherNoteId');
    }

    // Explicitly force resize to 25% (Right Panel) / 75% (PDF) regardless of previous state
    // This ensures that clicking "Ask Budlee AI" always resets the view to the optimal split
    // even if it was previously dragged or expanded.
    setTimeout(() => {
      if (rightPanelRef.current) {
        rightPanelRef.current.resize(25);
      }
    }, 0);
    try { trackEvent('learn_ask_budlee', attachUserProps({ chapter_id: selectedChapterId, teacher_note_id: teacherNoteId, page: 'learn' })); } catch (e) { }
  };

  const clearCitationHighlight = useCallback(() => {
    setCitationHighlights([]);
    setCitationHighlightedHighlightId(null);
  }, []);

  const handleCitationClick = useCallback((src) => {
    if (!src) return;
    
    // Handle figure-only clicks (when no page is provided)
    if (src.figure && !src.page) {
      // For figure-only references, we'll need to search the PDF for that figure
      // For now, just show a toast - you can implement figure search later
      console.log('Figure clicked:', src.figure);
      // You could add figure search functionality here similar to ChatPage
      return;
    }
    
    if (!src.page) return;
    
    const highlight = src.highlight || {};
    const highlightId = Date.now();
    const pageNum = Math.max(1, parseInt(src.page, 10) || 1);

    // Ensure the left PDF pane jumps to the cited page
    setCitationTargetPage(pageNum);

    setCitationHighlights([
      {
        id: highlightId,
        page_number: pageNum,
        text: highlight.text || '',
        section_name: highlight.section_name || '',
        context_before: highlight.context_before || '',
        context_after: highlight.context_after || '',
        disable_remove: true,
      },
    ]);
    setCitationHighlightedHighlightId(highlightId);
  }, []);

  const handleResetComplete = () => {
    // Force refresh of all components by updating refreshKey
    setRefreshKey(prev => prev + 1);
    // Also reload packState to ensure it's synced
    packState.reloadFromStorage();
  };

  return (
<div className="relative flex h-full flex-1 min-h-0 w-full bg-[hsl(var(--main-bg))] overflow-hidden">

      {/* Force selection panel when no subject/chapter selected - Only on mobile */}
      {(!selectedSubjectId || !selectedChapterId) && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="mx-auto w-full px-4 sm:px-6 max-w-2xl sm:max-w-3xl">
            <Card className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <h2 className="text-lg sm:text-xl font-bold text-[hsl(var(--main-bg))]">
                  Select Your Learning Focus
                </h2>
                <p className="text-sm sm:text-base text-[hsl(var(--main-bg))]/90">
                  Please select a subject and chapter to get started with your learning journey.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="flex-1 w-full">
                    <Select
                      value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                      onValueChange={handleSubjectChange}
                    >
                      <SelectTrigger className="bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base h-10 sm:h-12">
                        <SelectValue placeholder="Select Subject" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        {subjects.map((subject) => (
                          <SelectItem
                            key={subject.id}
                            value={subject.id.toString()}
                            className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                          >
                            {subject.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 w-full">
                    <Select
                      value={selectedChapterId ? selectedChapterId.toString() : ''}
                      onValueChange={handleChapterChange}
                      disabled={!selectedSubjectId}
                    >
                      <SelectTrigger className="bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base h-10 sm:h-12 disabled:opacity-50">
                        <SelectValue placeholder="Select Chapter" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        {chapters.length > 0 ? (
                          chapters.map((chapter) => (
                            <SelectItem
                              key={chapter.id}
                              value={chapter.id.toString()}
                              className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                            >
                              {chapter.label}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-chapters" disabled className="text-gray-400">
                            No chapters available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      ) : null}

      {/* Responsive layout: use simple stacked halves on small screens to guarantee top/bottom split */}
      {typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? (
        <div className="flex-1 w-full min-h-0 flex flex-col relative">
          <div className={cn(
            "w-full flex flex-col overflow-hidden relative transition-all duration-300 z-20",
            learningPathCollapsed ? "h-25" : rightPanelCollapsed ? "flex-1" : "h-1/2"
          )}>
            {/* Mobile: StatsBar - Above content */}
            <div className="flex-shrink-0 flex items-center justify-center p-2 bg-[hsl(var(--card-bg))] border-b border-[hsl(var(--card-border))]">
              <StatsBar totalXP={points?.total_xp ?? totalXP} />
            </div>

            {/* Chat Panel - Always mounted to preserve conversation state */}
            <div className={chatMode ? 'flex-1 h-full overflow-y-auto' : 'hidden'}>
              <BudleeAIChatPanel
                totalXP={points?.total_xp ?? totalXP}
                onClose={() => setChatMode(false)}
                prefilledText={prefilledText}
                onClearPrefilledText={() => setPrefilledText('')}
                prefilledAnchor={prefilledAnchor}
                onClearPrefilledAnchor={() => setPrefilledAnchor(null)}
                chapterId={selectedChapterId}
                subjectId={selectedSubjectId}
                pageContext="learn"
              />
            </div>

            {!learningPathCollapsed && !chatMode && (
              <>
                <LearningPath
                  refreshKey={refreshKey}
                  onXPEarned={handleXPEarned}
                  journeyMode={journeyMode}
                  onJourneyModeChange={handleJourneyModeChange}
                  learningPackData={learningPackData}
                  onOpenLearningPack={handleOpenLearningPack}
                  onSelectionChange={handleSelectionChange}
                  onToggleChatMode={handleToggleChatMode}
                  onAskBudleeAI={handleAskBudleeAI}
                  chatMode={chatMode}
                  isPanelExpanded={isPanelExpanded}
                  initialPdfPage={citationTargetPage}
                  citationHighlights={citationHighlights}
                  citationHighlightedHighlightId={citationHighlightedHighlightId}
                  // Mobile split props
                  showPdf={mobileShowPdf}
                  isMobileSplit={true}
                  isFullScreenJourney={mobileJourneyFullscreen}
                  onTogglePdf={() => setMobileShowPdf(v => !v)}
                  onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                  onSectionScroll={handlePDFSectionScroll}
                  onTextbookOpen={handleTextbookOpen}
                  onTextbookClose={handleTextbookClose}
                  // PDF state restoration
                  shouldShowTextbook={isPdfViewerOpen}
                  // Journey index management
                  currentJourneyIndex={currentJourneyIndex}
                  onJourneyIndexChange={setCurrentJourneyIndex}
                  mainContentOverride={
                    isLearningPackOpen ? (
                      <LearningPackViewer
                        embedded
                        isOpen={isLearningPackOpen}
                        onClose={handleCloseLearningPack}
                        activities={learningPackActivities}
                        chapterId={selectedChapterId}
                        onXPEarned={handleXPEarned}
                        currentIndex={learningPackCurrentIndex}
                        onIndexChange={setLearningPackCurrentIndex}
                      />
                    ) : null
                  }
                  navigationRequest={navigationRequest}
                  onNavigationRequestHandled={() => setNavigationRequest(null)}
                />
              </>
            )}
          </div>
          <div className={cn(
            "w-full overflow-auto border-t border-[hsl(var(--card-border))] transition-all duration-300",
            rightPanelCollapsed ? "h-25" : learningPathCollapsed ? "flex-1" : "h-1/2"
          )}>
            {!rightPanelCollapsed && (
              <RightPanel
                totalXP={points?.total_xp ?? totalXP}
                journeyMode={journeyMode}
                learningPackData={learningPackData}
                onStartJourney={handleStartJourney}
                isMobileSplit={true}
                isFullScreenJourney={mobileJourneyFullscreen}
                onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
                onTogglePdf={() => setMobileShowPdf(v => !v)}
                onOpenLearningPack={handleOpenLearningPack}
                learningPackOpen={isLearningPackOpen}
                learningPackCurrentIndex={learningPackCurrentIndex}
                currentSelection={{ subject: selectedSubject, chapter: selectedChapter }}
                chapterId={selectedChapterId}
                subjectId={selectedSubjectId}
                chatMode={chatMode}
                onChatClose={handleCloseChatMode}
                prefilledText={prefilledText}
                onClearPrefilledText={() => setPrefilledText('')}
                prefilledAnchor={prefilledAnchor}
                onClearPrefilledAnchor={() => setPrefilledAnchor(null)}
                onResetComplete={handleResetComplete}
                onCitationClick={handleCitationClick}
                onBeforeSend={clearCitationHighlight}
                onAskBudleeAI={handleAskBudleeAI}
                showNotesPanel={showNotesPanel}
                setShowNotesPanel={setShowNotesPanel}
                teacherNotes={teacherNotes}
                isLoadingNotes={isLoadingNotes}
                mergedSectionKeys={mergedSectionKeys}
              />
            )}
          </div>
        </div>
      ) : (
        <ResizablePanelGroup direction="horizontal" className="flex-1 w-full min-h-0 flex flex-col md:flex-row">
          <ResizablePanel
            defaultSize={70}
            minSize={60}
            className={cn(
              "flex flex-col min-h-0 overflow-hidden h-1/2 md:h-full w-full md:w-auto overflow-auto md:overflow-hidden",
              !chatMode && "flex-1",
              // Ensure Left Panel retains 60% width when split, even if Right Panel pushes
              isPanelExpanded && "!min-w-[60%]",
              // Ensure proper pointer events for left panel
              "pointer-events-auto"
            )}
          >
            <div className="h-full w-full flex flex-col overflow-hidden min-h-0">
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                <LearningPath
                  refreshKey={refreshKey}
                  onXPEarned={handleXPEarned}
                  journeyMode={journeyMode}
                  onJourneyModeChange={handleJourneyModeChange}
                  learningPackData={learningPackData}
                  onOpenLearningPack={handleOpenLearningPack}
                  onSelectionChange={handleSelectionChange}
                  onToggleChatMode={handleToggleChatMode}
                  onAskBudleeAI={handleAskBudleeAI}
                  chatMode={chatMode}
                  isPanelExpanded={isPanelExpanded}
                  initialPdfPage={citationTargetPage}
                  citationHighlights={citationHighlights}
                  citationHighlightedHighlightId={citationHighlightedHighlightId}
                  mainContentOverride={
                    isLearningPackOpen ? (
                      <LearningPackViewer
                        embedded
                        isOpen={isLearningPackOpen}
                        onClose={handleCloseLearningPack}
                        activities={learningPackActivities}
                        chapterId={selectedChapterId}
                        onXPEarned={handleXPEarned}
                        currentIndex={learningPackCurrentIndex}
                        onIndexChange={setLearningPackCurrentIndex}
                      />
                    ) : null
                  }
                  navigationRequest={navigationRequest}
                  onNavigationRequestHandled={() => setNavigationRequest(null)}
                  onSectionScroll={handlePDFSectionScroll}
                  onTextbookOpen={handleTextbookOpen}
                  onTextbookClose={handleTextbookClose}
                  // Journey index management
                  currentJourneyIndex={currentJourneyIndex}
                  onJourneyIndexChange={setCurrentJourneyIndex}
                />
              </div>
            </div>
          </ResizablePanel>

          {isPanelExpanded && <ResizableHandle withHandle />}

          <ResizablePanel
            ref={rightPanelRef}
            defaultSize={isPanelExpanded ? 25 : 25}
            maxSize={40}
            minSize={25}
            className={cn(
              !isPanelExpanded && "!w-96 !flex-none shrink-0",
              // Mobile half-height behavior
              "h-1/2 md:h-full w-full md:w-auto overflow-auto md:overflow-hidden",
              // Force flex item width constraint via CSS
              isPanelExpanded && "!max-w-[40%]",
              // Force flex item to respect width constraints even with wide content (e.g. Latex)
              "min-w-0 overflow-hidden"
            )}
            style={!isPanelExpanded ? { flex: 'none', width: '24rem' } : undefined}
            id="right-panel-resize-container"
            order={2}
          >
            <RightPanel
              totalXP={points?.total_xp ?? totalXP}
              journeyMode={journeyMode}
              learningPackData={learningPackData}
              onStartJourney={handleStartJourney}
              // Mobile split props
              isMobileSplit={true}
              isFullScreenJourney={mobileJourneyFullscreen}
              onRequestFullJourney={() => setMobileJourneyFullscreen(true)}
              onTogglePdf={() => setMobileShowPdf(v => !v)}
              onOpenLearningPack={handleOpenLearningPack}
              learningPackOpen={isLearningPackOpen}
              learningPackCurrentIndex={learningPackCurrentIndex}
              currentSelection={{ subject: selectedSubject, chapter: selectedChapter }}
              chapterId={selectedChapterId}
              subjectId={selectedSubjectId}
              chatMode={chatMode}
              onChatClose={handleCloseChatMode}
              prefilledText={prefilledText}
              onClearPrefilledText={() => setPrefilledText('')}
              prefilledAnchor={prefilledAnchor}
              onClearPrefilledAnchor={() => setPrefilledAnchor(null)}
              prefillAndSendText={prefillAndSendText}
              onClearPrefillAndSend={() => setPrefillAndSendText('')}
              onResetComplete={handleResetComplete}
              onCitationClick={handleCitationClick}
              onBeforeSend={clearCitationHighlight}
              onAskBudleeAI={handleAskBudleeAI}
              showNotesPanel={showNotesPanel}
              setShowNotesPanel={setShowNotesPanel}
              teacherNotes={teacherNotes}
              isLoadingNotes={isLoadingNotes}
              mergedSectionKeys={mergedSectionKeys}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      )}

      {/* Floating Progress Button - Only on mobile */}
      {typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleProgressToggle}
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-50 bg-[hsl(var(--green-bright))] border-[hsl(var(--card-border))] text-black hover:bg-[hsl(var(--green-bright))]/80 shadow-lg"
        >
          {chatMode
            ? (previousView === 'Learning Path' ? 'View Learning Path' :
               previousView === 'PDF Section Viewer' ? 'View PDF' :
               previousView === 'Card View Journey' ? 'View Learning Cards' :
               previousView)
            : (rightPanelCollapsed ? 'View Learning Packs' : 
               previousView === 'Learning Path' ? 'View Learning Path' :
               previousView === 'PDF Section Viewer' ? 'View PDF' :
               previousView === 'Card View Journey' ? 'View Learning Cards' :
               previousView)
          }
        </Button>
      )}

    </div>
  );
}

